import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Col, Modal, Row, Spinner, Tab, Tabs, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { listMatchRoster } from '../../../../lib/api/match';

const getClubImageUrl = imagePath =>
  imagePath
    ? `${process.env.REACT_APP_BASE_API_URL}/uploads/${imagePath}`
    : null;

const getImageUrl = imagePath =>
  imagePath
    ? `${process.env.REACT_APP_BASE_API_URL}/uploads/${imagePath}`
    : null;

const formatDateValue = value => {
  if (!value) {
    return '';
  }

  const plainValue = String(value).split('T')[0];
  const [year, month, day] = plainValue.split('-');

  if (year && month && day) {
    return `${day}.${month}.${year}`;
  }

  return value;
};

const toRowList = response =>
  Array.isArray(response) ? response : response?.items || [];

const getPlayerCount = players => players.length;

const getRowIdentity = row =>
  String(row?.match_roster_guid || row?.entities_id || row?.shirt_number || row?.ent_name || '');

const CheckCell = ({ value }) => (
  <td className="text-center">
    {Number(value) === 1 || value === true ? (
      <FontAwesomeIcon icon="check" className="text-success" />
    ) : (
      ''
    )}
  </td>
);

const InfoRow = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 text-700 fs-10">
    <span className="text-600">{label}</span>
    <span className="text-end">{value || '-'}</span>
  </div>
);

const LicenseOverviewModal = ({ show, matchItem, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [previewImage, setPreviewImage] = useState(null);
  const [clubData, setClubData] = useState({
    home: { players: [], staff: [] },
    away: { players: [], staff: [] }
  });
  const [selectedRows, setSelectedRows] = useState({
    home: null,
    away: null
  });

  const clubs = useMemo(
    () => [
      {
        key: 'home',
        clubGuid: matchItem?.club_1_guid,
        clubName: matchItem?.club_1_name,
        clubImagePath: matchItem?.club_1_image_path
      },
      {
        key: 'away',
        clubGuid: matchItem?.club_2_guid,
        clubName: matchItem?.club_2_name,
        clubImagePath: matchItem?.club_2_image_path
      }
    ],
    [matchItem]
  );

  useEffect(() => {
    setPreviewImage(null);

    if (!show || !matchItem?.matches_guid) {
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setActiveTab('home');

      try {
        const responses = await Promise.all(
          clubs.flatMap(club => [
            listMatchRoster({
              matches_guid: matchItem.matches_guid,
              role: 'player',
              club_guid: club.clubGuid
            }),
            listMatchRoster({
              matches_guid: matchItem.matches_guid,
              role: 'staff',
              club_guid: club.clubGuid
            })
          ])
        );

        const nextClubData = {
          home: {
            players: toRowList(responses[0]),
            staff: toRowList(responses[1])
          },
          away: {
            players: toRowList(responses[2]),
            staff: toRowList(responses[3])
          }
        };

        setClubData(nextClubData);
        setSelectedRows({
          home:
            nextClubData.home.players[0]
              ? { type: 'player', row: nextClubData.home.players[0] }
              : nextClubData.home.staff[0]
                ? { type: 'staff', row: nextClubData.home.staff[0] }
                : null,
          away:
            nextClubData.away.players[0]
              ? { type: 'player', row: nextClubData.away.players[0] }
              : nextClubData.away.staff[0]
                ? { type: 'staff', row: nextClubData.away.staff[0] }
                : null
        });
      } catch (error) {
        console.error('Greska prilikom ucitavanja licenci za utakmicu:', error);
        setClubData({
          home: { players: [], staff: [] },
          away: { players: [], staff: [] }
        });
        setSelectedRows({
          home: null,
          away: null
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [show, matchItem, clubs]);

  const handleClose = () => {
    setPreviewImage(null);
    setClubData({
      home: { players: [], staff: [] },
      away: { players: [], staff: [] }
    });
    setActiveTab('home');
    setSelectedRows({
      home: null,
      away: null
    });
    setLoading(false);
    onClose();
  };

  const handleSelectRow = (tabKey, type, row) => {
    setSelectedRows(prev => ({
      ...prev,
      [tabKey]: { type, row }
    }));
  };

  const isSelectedRow = (tabKey, type, row) => {
    const selected = selectedRows[tabKey];

    if (!selected || selected.type !== type) {
      return false;
    }

    return getRowIdentity(selected.row) === getRowIdentity(row);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onEscapeKeyDown={event => {
        if (previewImage) {
          event.preventDefault();
          setPreviewImage(null);
        }
      }}
      size="xl"
    >
      <Modal.Header closeButton className="bg-200">
        <Modal.Title>Pregled licenci</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onSelect={key => {
              setPreviewImage(null);
              setActiveTab(key || 'home');
            }}
            id="license-overview-tabs"
            className="mb-3"
          >
            {clubs.map(club => {
              const currentClubData = clubData[club.key] || {
                players: [],
                staff: []
              };
              const selectedItem = selectedRows[club.key]?.row || null;
              const selectedImageUrl = getImageUrl(selectedItem?.image_path);

              return (
                <Tab
                  key={club.key}
                  eventKey={club.key}
                  title={
                    <span className="d-inline-flex align-items-center gap-2">
                      {club.clubImagePath && (
                        <img
                          src={getClubImageUrl(club.clubImagePath)}
                          alt={club.clubName || 'Club'}
                          style={{
                            width: 20,
                            height: 20,
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                        />
                      )}
                      <span>{club.clubName || '-'}</span>
                      <Badge bg="secondary" pill>
                        {getPlayerCount(currentClubData.players)}
                      </Badge>
                    </span>
                  }
                >
                  <Row className="g-4">
                    <Col lg={8} className="d-none d-lg-block">
                      <div className="mb-3">
                        <Table responsive bordered hover>
                          <thead className="bg-200">
                            <tr>
                              <th className="text-end">Dres</th>
                              <th>Ime i prezime</th>
                              <th className="text-center">Datum</th>
                              <th className="text-center">Selekcija</th>
                              <th className="text-center">Libero</th>
                              <th className="text-center">Kapiten</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentClubData.players.length > 0 ? (
                              currentClubData.players.map(player => (
                                <tr
                                  key={getRowIdentity(player)}
                                  onClick={() =>
                                    handleSelectRow(club.key, 'player', player)
                                  }
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelectedRow(club.key, 'player', player)
                                      ? 'rgba(44, 123, 229, 0.12)'
                                      : 'transparent'
                                  }}
                                >
                                  <td className="text-end">{player.shirt_number ?? ''}</td>
                                  <td>{`${player.name || ''} ${player.last_name || ''}`.trim()}</td>
                                  <td className="text-center">{formatDateValue(player.date_of_birth)}</td>
                                  <td className="text-center">{player.selection_name || ''}</td>
                                  <CheckCell value={player.is_libero} />
                                  <CheckCell value={player.is_captain} />
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="text-center text-body-tertiary py-3">
                                  Nema unesenih igraca.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>

                      <div>
                        <Table responsive bordered hover>
                          <thead className="bg-200">
                            <tr>
                              <th>Status</th>
                              <th>Ime i prezime</th>
                              <th className="text-center">Licenca</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentClubData.staff.length > 0 ? (
                              currentClubData.staff.map(staff => (
                                <tr
                                  key={getRowIdentity(staff)}
                                  onClick={() =>
                                    handleSelectRow(club.key, 'staff', staff)
                                  }
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelectedRow(club.key, 'staff', staff)
                                      ? 'rgba(44, 123, 229, 0.12)'
                                      : 'transparent'
                                  }}
                                >
                                  <td>{staff.type_name || ''}</td>
                                  <td>{staff.ent_name || ''}</td>
                                  <td className="text-center">{staff.license_name || ''}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={3} className="text-center text-body-tertiary py-3">
                                  Nema unesenog staffa.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Col>

                    <Col lg={4} className="d-none d-lg-block">
                      <div className="border rounded bg-body-tertiary p-3 h-100">
                        {selectedImageUrl ? (
                          <img
                            src={selectedImageUrl}
                            alt={selectedItem?.ent_name || selectedItem?.name || 'Slika'}
                            style={{
                              width: '100%',
                              maxHeight: 420,
                              objectFit: 'contain',
                              borderRadius: 8,
                              backgroundColor: '#fff'
                            }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center text-center text-body-tertiary border rounded bg-white"
                            style={{ minHeight: 320 }}
                          >
                            Nema dostupne slike za izabrani red.
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col xs={12} className="d-lg-none">
                      <div className="mb-3">
                        <h6 className="mb-3">Igrači</h6>
                        <Row className="g-3">
                          {currentClubData.players.length > 0 ? (
                            currentClubData.players.map(player => {
                              const playerImageUrl = getImageUrl(player.image_path);
                              const playerName = `${player.name || ''} ${player.last_name || ''}`.trim() || 'Igrač';
                              return (
                                <Col xs={12} key={getRowIdentity(player)}>
                                  <div className="border rounded-4 bg-white h-100 p-3 shadow-sm">
                                    {playerImageUrl ? (
                                      <div className="text-center">
                                        <button
                                          type="button"
                                          className="border-0 p-0 bg-transparent rounded-circle"
                                          aria-label={`Prikaži cijelu sliku: ${playerName}`}
                                          onClick={() =>
                                            setPreviewImage({
                                              src: playerImageUrl,
                                              alt: playerName
                                            })
                                          }
                                          style={{ cursor: 'zoom-in' }}
                                        >
                                          <img
                                            src={playerImageUrl}
                                            alt={playerName}
                                            style={{
                                              width: 140,
                                              height: 140,
                                              objectFit: 'cover',
                                              borderRadius: '50%',
                                              backgroundColor: '#fff'
                                            }}
                                          />
                                        </button>
                                      </div>
                                    ) : (
                                      <div
                                        className="d-flex align-items-center justify-content-center text-center text-body-tertiary border rounded-circle bg-white mx-auto"
                                        style={{ width: 140, height: 140 }}
                                      >
                                        Nema slike
                                      </div>
                                    )}

                                    <div className="mt-3 text-center">
                                      <div className="fw-semibold text-dark fs-8">
                                        {`${player.name || ''} ${player.last_name || ''}`.trim() || '-'}
                                      </div>
                                    </div>

                                    <div className="mt-3 d-flex flex-column gap-2">
                                      <InfoRow label="Dres" value={player.shirt_number ?? '-'} />
                                      <InfoRow label="Datum" value={formatDateValue(player.date_of_birth)} />
                                      <InfoRow label="Selekcija" value={player.selection_name} />
                                      <InfoRow
                                        label="Libero"
                                        value={Number(player.is_libero) === 1 || player.is_libero === true ? 'Da' : 'Ne'}
                                      />
                                      <InfoRow
                                        label="Kapiten"
                                        value={Number(player.is_captain) === 1 || player.is_captain === true ? 'Da' : 'Ne'}
                                      />
                                    </div>
                                  </div>
                                </Col>
                              );
                            })
                          ) : (
                            <Col xs={12}>
                              <div className="text-center text-body-tertiary border rounded bg-white py-4">
                                Nema unesenih igraca.
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>

                      <div>
                        <h6 className="mb-3">Klupa</h6>
                        <Row className="g-3">
                          {currentClubData.staff.length > 0 ? (
                            currentClubData.staff.map(staff => {
                              const staffImageUrl = getImageUrl(staff.image_path);
                              return (
                                <Col xs={12} key={getRowIdentity(staff)}>
                                  <div className="border rounded-4 bg-white h-100 p-3 shadow-sm">
                                    {staffImageUrl ? (
                                      <div className="text-center">
                                        <img
                                          src={staffImageUrl}
                                          alt={staff.ent_name || 'Staff'}
                                          style={{
                                            width: 140,
                                            height: 140,
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                            backgroundColor: '#fff'
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <div
                                        className="d-flex align-items-center justify-content-center text-center text-body-tertiary border rounded-circle bg-white mx-auto"
                                        style={{ width: 140, height: 140 }}
                                      >
                                        Nema slike
                                      </div>
                                    )}

                                    <div className="mt-3 text-center">
                                      <div className="fw-semibold text-dark fs-8">
                                        {staff.ent_name || '-'}
                                      </div>
                                    </div>

                                    <div className="mt-3 d-flex flex-column gap-2">
                                      <InfoRow label="Status" value={staff.type_name} />
                                      <InfoRow label="Licenca" value={staff.license_name} />
                                    </div>
                                  </div>
                                </Col>
                              );
                            })
                          ) : (
                            <Col xs={12}>
                              <div className="text-center text-body-tertiary border rounded bg-white py-4">
                                Nema unesenog staffa.
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Tab>
              );
            })}
          </Tabs>
        )}
        {show && previewImage && (
          <button
            type="button"
            autoFocus
            className="d-lg-none position-fixed top-0 start-0 w-100 h-100 border-0 p-3"
            aria-label="Zatvori prikaz cijele slike"
            onClick={() => setPreviewImage(null)}
            style={{
              zIndex: 1060,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              cursor: 'zoom-out'
            }}
          >
            <img
              src={previewImage.src}
              alt={previewImage.alt}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </button>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LicenseOverviewModal;
